import { useCallback, useState } from "react";
import { Obra } from "../../domain/models/obra";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { useObrasTopHeaderState } from "../components/hooks/obras_top_header_state";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useLocation } from "react-router";
import { useGestaoResiduosListaPagination } from "../hooks/Obras_gestaoresiduos/pagination";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { GetAllObrasGestaoDeResiduosData } from "../../data/daos/read/get_all_obras_gestao_de_residuos";
import { GestaoResiduosForm } from "../schemas/Obras_gestao_residuos/gestaoderesiduos";
import { useObrasCommonReports } from "../components/hooks/obras_common_reports";
import { TelasIds } from "../../../../common/utils/tela_ids";
import { downloadBase64File } from "../../../../common/utils/download_utils";

type LoadObraFuncState = {
  idObra?: number;
};
type LoadFilterFuncState = {
  IdObra?: number | null;
  CurrentPage?: number | null;
  RowsPerPage?: number | null;
};

export const useObrasGestaoResiduosViewModel = () => {
  const {
    obras: {
      getObraByIdUseCase,
      getAllObrasGestaoDeResiduosUseCase,
      createObrasGestaoDeResiduosUseCase,
      updateObrasGestaoDeResiduosUseCase,
      deleteObrasGestaoDeResiduosUseCase,
      getByIdGestaoDeResiduosUseCase,
    },
  } = useDependencyInjection();
  const [Obrasgestaoresiduos, setObrasgestaoresiduos] = useState<GetAllObrasGestaoDeResiduosData[]>([]);
  const pagination = useGestaoResiduosListaPagination();
  const { pushErrorNotification } = usePageLoader();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [selectedObra, setSelectedObra] = useState<Obra>();
  const [idObra, setIdObra] = useState<number>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const obrasTopHeaderState = useObrasTopHeaderState();
  const [editmod, seteditmod] = useState(false);
  const backOrNavigate = useBackOrNavigate();
  const [abrirModal, setAbrirModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [IdGestaoDeResiduos, setIdGestaoDeResiduos] = useState<number>();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [dadosEdit, setDadosEdit] = useState<GestaoResiduosForm | null>(null);
  const [obrasReportState, initializeReportState] = useObrasCommonReports({ idObra });
  //#region Loads

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationId = params.get("id");
      const id = locationId ?? undefined;

      if (!id || id.length === 0) {
        setErrorMessage("Falha ao recuperar id da obra!");
        return;
      }
      setIdObra(Number(id));
      loadObra({ idObra: Number(id) });
      initializeReportState(Number(id));
      await load({ IdObra: Number(id) });
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados das paginas!")[0]);
    }
  });

  //#endregion

  //#region Getall
  const load = useCallback(
    async (funcState?: LoadFilterFuncState) => {
      try {
        const filterpage = funcState?.CurrentPage ?? pagination.page;
        const filterRowspage = funcState?.RowsPerPage ?? pagination.rowsPerPage;
        const filteridObra = idObra ?? funcState?.IdObra ?? null;

        await getAllObrasGestaoDeResiduosUseCase.getAllObrasGestaoDeResiduos(filteridObra, filterpage, filterRowspage);
        setObrasgestaoresiduos(getAllObrasGestaoDeResiduosUseCase.items);
        pagination.setRowsPerPage(getAllObrasGestaoDeResiduosUseCase.pageSize);
        pagination.setTotalItems(getAllObrasGestaoDeResiduosUseCase.totalItems);
      } catch (e: unknown) {
        throw new Error("Falha ao buscar motivos de afastamento!");
      }
    },
    [getAllObrasGestaoDeResiduosUseCase, idObra, pagination],
  );
  //#endregion
  const handleDownload = useCallback(
    async (arquivoNome: string, arquivo: string) => {
      try {
        await downloadBase64File(arquivoNome, arquivo);
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao fazer download do arquivo!"));
      }
    },
    [pushErrorNotification],
  );
  //#region Create/Update

  const handleDocumentoSubmit = useCallback(
    async (form: GestaoResiduosForm) => {
      try {
        setSaving(true);
        if (editmod == false) {
          await createObrasGestaoDeResiduosUseCase.createObrasGestaoDeResiduos(
            idObra ?? form.idObra,
            form.classe,
            form.caracteristica,
            form.tipo,
            form.formaSegregacao,
            form.empresaGerenciadora,
            form.destinacaoFinal,
            form.arquivo,
            form.nomeArquivo,
          );
          await load();
        } else {
          await updateObrasGestaoDeResiduosUseCase.updateObrasGestaoDeResiduos(
            form.idGestaoResiduos,
            idObra ?? form.idObra,
            form.classe,
            form.caracteristica,
            form.tipo,
            form.formaSegregacao,
            form.empresaGerenciadora,
            form.destinacaoFinal,
            form.arquivo,
            form.nomeArquivo,
          );
          await load();
        }
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar documentos!"));
      } finally {
        setSaving(false);
        setAbrirModal(false);
      }
    },
    [createObrasGestaoDeResiduosUseCase, updateObrasGestaoDeResiduosUseCase, idObra, editmod],
  );

  //#endregion

  //#region Delete

  const DeleteOpenModal = useCallback(
    (id: number | null, openState: boolean) => {
      if (openState) {
        if (id != null) {
          setIdGestaoDeResiduos(id);
        } else {
          setIdGestaoDeResiduos(undefined);
        }
      }
      setOpenDeleteModal(openState);
    },
    [IdGestaoDeResiduos, setIdGestaoDeResiduos],
  );

  const DeletarGestaoResiduos = useCallback(async () => {
    setLoading(true);
    if (IdGestaoDeResiduos === undefined) return;
    try {
      await deleteObrasGestaoDeResiduosUseCase.deleteObrasGestaoDeResiduos(IdGestaoDeResiduos);
      await load();
    } catch {
    } finally {
      setLoading(false);
      setOpenDeleteModal(false);
    }
  }, [IdGestaoDeResiduos, deleteObrasGestaoDeResiduosUseCase]);

  //#endregion

  //#region GetById

  function createFileFromResponse(fileName: string | null, base64Data: string | null): File | null {
    if (!base64Data) return null;

    try {
      const byteString = atob(base64Data.split(",")[1] ?? "");
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);

      return new File([ab], fileName || "arquivo", { type: "application/octet-stream" });
    } catch {
      return null;
    }
  }

  const getbyid = useCallback(
    async (id: number | null) => {
      setLoading(true);
      try {
        if (id != null) {
          const response = await getByIdGestaoDeResiduosUseCase.getByIdGestaoDeResiduos(id);
          const arquivo = createFileFromResponse(response.nomeArquivo ?? null, response.arquivo ?? null);
          setDadosEdit({
            ...response,
            arquivo: arquivo,
          });
        }
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar documento!"));
      } finally {
        setLoading(false);
      }
    },
    [getByIdGestaoDeResiduosUseCase, setDadosEdit, dadosEdit],
  );

  const handleEditMaquinasEquipamentoOpenState = useCallback(
    (idEquipMedicao: number | null, openState: boolean) => {
      if (openState) {
        getbyid(idEquipMedicao);
        setAbrirModal(true);
        seteditmod(true);
      }
    },
    [getbyid, editmod],
  );

  //#endregion

  const loadObra = useCallback(
    async (funcState?: LoadObraFuncState) => {
      try {
        const filteredIdObra = funcState?.idObra ?? idObra;

        const obra = await getObraByIdUseCase.getObraById(TelasIds.Obras_gestaoresiduos, filteredIdObra);

        setSelectedObra(obra);
      } catch (e: unknown) {
        setSelectedObra(undefined);
        throw e;
      }
    },
    [idObra, getObraByIdUseCase],
  );

  const handlePageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        useGestaoResiduosListaPagination.setState({ page: page + 1 });
        //await load({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
      } finally {
        setLoading(false);
      }
    },
    [pushErrorNotification, load],
  );

  const handleRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        useGestaoResiduosListaPagination.setState({
          page: 1,
          rowsPerPage,
        });
        await load({ CurrentPage: 1, RowsPerPage: rowsPerPage });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
      } finally {
        setLoading(false);
      }
    },
    [pushErrorNotification, load],
  );

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: "/Obras_lista" });
  }, [backOrNavigate]);

  const abrirModalCreate = useCallback(() => {
    setAbrirModal(true);
    seteditmod(false);
    setDadosEdit(null);
    setDadosEdit({ idObra: idObra ?? 0 });
  }, [setAbrirModal, seteditmod, setDadosEdit, dadosEdit]);

  const fecharmodal = useCallback(() => {
    setAbrirModal(false);
    setDadosEdit(null);
  }, [setDadosEdit, dadosEdit]);

  return {
    loading,
    selectedObra: selectedObra ?? null,
    obrasTopHeaderState,
    pagination,
    Obrasgestaoresiduos,
    abrirModal,
    openDeleteModal,
    dadosEdit,
    editmod,
    saving,
    obrasReportState,
    load,
    handleBack,
    handlePageChange,
    handleRowsPerPageChange,
    abrirModalCreate,
    fecharmodal,
    handleDocumentoSubmit,
    DeleteOpenModal,
    DeletarGestaoResiduos,
    handleEditMaquinasEquipamentoOpenState,
    handleDownload,
  };
};
